/* eslint-disable no-restricted-syntax */
import i18n from 'i18next';
import { getFontFamilyConfigData } from '@src/framework/editor/src/js/constants/font-list';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { isCustomFont } from '@src/utils/fonts';
import { SlideInterface } from '@tencent/docs-api';
import { BaseAnalyzer } from '../base/base-analyzer';
import { IReview, MachineReviewAnalyzer } from './machine-review-analyzer';
import { copyRightFont } from './constants';

type FontName = string;

interface ISingleFont {
    elements: Set<IElementBaseModel>;
    isLocal: boolean,
    isCustom: boolean,
    hasCopyRight: boolean,
}

type IResult = SlideInterface.CheckResultItem;

export class MachineFontAnalyzer extends BaseAnalyzer<IResult> {
    private title = i18n.t('字体版权检测');
    private description = i18n.t('检查所使用的字体是否存在版权风险');
    private level = 'error' as const;

    private eaFontsResults = new Map<FontName, ISingleFont>();
    private latinFontsResults = new Map<FontName, ISingleFont>();

    public constructor(private reviewAnalyzer: MachineReviewAnalyzer) {
        super();
    }

    public onPresentationStart(): void {
        this.eaFontsResults = new Map<FontName, ISingleFont>();
        this.latinFontsResults = new Map<FontName, ISingleFont>();
    }

    public async calculateResult() {
        const review = (await this.reviewAnalyzer.result) as IReview;
        // 把所有字体提取出来
        for (const page of review.pages) {
            for (const paragraph of page.bodyTexts) {
                for (const textLine of paragraph.textLines) {
                    if (textLine.lang === 'ea') {
                        await this.addFontElement(this.eaFontsResults, textLine.ea, paragraph.element);
                    }
                    if (textLine.lang === 'latin') {
                        await this.addFontElement(this.latinFontsResults, textLine.latin, paragraph.element);
                    }
                }
            }
        }

        // 重新组织数据结构
        const errorMessages: SlideInterface.CheckErrorMessage[] = [];
        for (const [font, fontInfo] of this.eaFontsResults) {
            this.addErrorMessages(font, fontInfo, errorMessages);
        }
        for (const [font, fontInfo] of this.latinFontsResults) {
            this.addErrorMessages(font, fontInfo, errorMessages);
        }
        const passed = errorMessages.length <= 0;
        if (passed) {
            return { passed, title: this.title, description: this.description };
        }
        return { passed, title: this.title, description: this.description, errorMessages };
    }

    private addErrorMessages(font: string, fontInfo: ISingleFont, errorMessages: SlideInterface.CheckErrorMessage[]) {
        const anchorType = 'element';
        if (!fontInfo.hasCopyRight) {
            fontInfo.elements.forEach((element) => {
                errorMessages.push({
                    level: this.level,
                    anchorType,
                    anchor: element,
                    description: i18n.t(`元素使用的字体[${font}]有版权风险，建议替换为其他字体`),
                });
            });
        }
    }

    private async addFontElement(
        fontResults: Map<FontName, ISingleFont>,
        fontFamily: string,
        element?: IElementBaseModel,
    ) {
        if (!element) {
            return;
        }
        const fontElementsSets = fontResults.get(fontFamily)?.elements;

        // 元素字体已经使用过，直接添加在现有元素集合里
        if (fontElementsSets) {
            fontElementsSets.add(element);
            return;
        }

        // 元素字体没有使用过，判断版权信息并创建元素集合
        const font = this.transformFont(fontFamily).toLowerCase();
        const isFontCustom = await isCustomFont(fontFamily);
        const isFontLocal = getFontFamilyConfigData().some(({ id, content }) =>
            [id.toLowerCase(), content.toLowerCase()].includes(font));
        const isFontLocalExtra = copyRightFont.some(font => font.toLowerCase() === font);
        const isDefault = ['默认字体'].includes(font);
        fontResults.set(fontFamily, {
            elements: new Set([element]),
            isLocal: isFontLocal,
            isCustom: isFontCustom,
            hasCopyRight: isFontLocal || isFontCustom || isFontLocalExtra || isDefault,
        });
    }

    /**
     * 字体判断的一些特殊逻辑
     */
    private transformFont(textFont: string): string {
        if (textFont === '宋体' || textFont === 'Songti SC') {
            return 'SimSun';
        }
        if (textFont === 'Heiti SC') {
            return 'SimHei';
        }
        if (textFont === 'KaiTi SC') {
            return 'KaiTi';
        }
        if (textFont === '等线' || textFont === '默认字体') {
            return 'Arial';
        }
        return textFont;
    }
}
